import { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import { withTemplate } from "@/components/TemplatePicker";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlusIcon, XIcon, ProhibitIcon, EyeIcon, PrinterIcon, EnvelopeIcon, MagnifyingGlassIcon, PackageIcon, WarningIcon, FilePdfIcon } from "@phosphor-icons/react";
import { EmailHistory, SendDocEmailModal } from "@/components/DocEmail";
import { ClientePicker, FacturasCliente, CuentaSelect } from "./SeleccionOrigen";
import CNTraceability from "./CNTraceability";
import { usePeriodQuery, PeriodToolbar, Pager, fmtEmision } from "@/components/PeriodQuery";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import TrasladosNc from "@/pages/_credit/TrasladosNc";
import { useGestor } from "@/pages/_payments/CarteraCliente";
import DocumentoPdfModal from "@/components/DocumentoPdfModal";

/**
 * Sección "Notas de crédito" de Ventas y facturación: consultar, buscar, crear (desde una factura
 * registrada o por ingreso manual), borradores, visualizar, imprimir/exportar PDF y anular.
 */
/** Notas de crédito: documentos emitidos y, para Administrador y Delegado, los traslados a subcuenta NC
 *  (Pendientes de aprobación · Pendientes de generar · Historial). */
export default function CreditNotesSection() {
  const { gestor } = useGestor();
  const [vista, setVista] = useState(() => { try { return sessionStorage.getItem("agro_nc_vista") || "documentos"; } catch { return "documentos"; } });
  const cambiar = (v) => { setVista(v); try { sessionStorage.setItem("agro_nc_vista", v); } catch { /* sin almacenamiento */ } };
  const VISTAS = [["documentos", "Documentos emitidos"], ...(gestor ? [["aprobacion", "Pendientes de aprobación"], ["generar", "Pendientes de generar"], ["historial", "Historial"]] : [])];
  const actual = VISTAS.some(([k]) => k === vista) ? vista : "documentos";
  return (
    <div className="space-y-4">
      {VISTAS.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 border-b border-border/60" role="tablist" aria-label="Notas de crédito" data-testid="nc-vistas">
          {VISTAS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={actual === k} onClick={() => cambiar(k)} data-testid={`nc-vista-${k}`}
              className={`min-h-[44px] px-3 text-sm whitespace-nowrap border-b-2 -mb-px ${actual === k ? "border-agro-green text-agro-green font-semibold" : "border-transparent text-text-secondary"}`}>{l}</button>
          ))}
        </div>
      )}
      {actual === "documentos" ? <DocumentosNc /> : (
        <div>
          <p className="text-xs text-text-secondary mb-3">Traslados de saldo a la subcuenta NC del cliente. Trasladar no emite la nota de crédito ni registra un pago: la nota se genera de forma explícita una vez aprobado el traslado.</p>
          <TrasladosNc bandeja={actual} />
        </div>
      )}
    </div>
  );
}

function DocumentosNc() {
  const { hasModule } = useAuth();
  const { admin } = useGestor();
  const puedeCrear = !hasModule || hasModule("ventas", "crear");
  const puedeAnular = !hasModule || hasModule("ventas", "eliminar");
  const [drafts, setDrafts] = useState([]);
  const [estado, setEstado] = useState("");
  const pq = usePeriodQuery("notas-credito", { estado });
  const [modal, setModal] = useState(null);        // {draft?} → abre el formulario
  const [viewing, setViewing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/credit-notes-drafts");
      setDrafts(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    pq.reload();
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const anular = async (cn) => {
    if (!window.confirm(`¿Anular ${cn.numero}? Se revierte su efecto sobre el documento, el inventario y el saldo a favor generado. El correlativo no se reutiliza.`)) return;
    try { await api.post(`/credit-notes/${cn.id}/void`); toast.success("Nota de crédito anulada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const emitirBorrador = async (d) => {
    if (!window.confirm("¿Emitir este borrador? Tomará el CAI y el correlativo vigentes y aplicará su efecto en la cuenta del cliente.")) return;
    try { const { data } = await api.post(`/credit-notes-drafts/${d.id}/emit`); toast.success(`Emitida ${data.numero}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const borrarBorrador = async (d) => {
    if (!window.confirm("¿Eliminar este borrador?")) return;
    try { await api.delete(`/credit-notes-drafts/${d.id}`); toast.success("Borrador eliminado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4" data-testid="section-notas-credito">
      <SectionHeader title="NOTAS DE CRÉDITO"
        desc="Devoluciones, descuentos y ajustes sobre facturas o notas de débito. Usan su propia autorización fiscal y correlativo."
        action={puedeCrear && (
          <CreateButton onClick={() => setModal({})} testid="new-cn-btn" label="CREAR NOTA DE CRÉDITO" />
        )} />
      <PeriodToolbar pq={pq} saldo={false} testid="cn-period">
        <EstadoSelect value={estado} onChange={setEstado} />
      </PeriodToolbar>
      {drafts.length > 0 && (
        <DraftsTable drafts={drafts} refLabel="Documento de origen" testid="cn-drafts"
          onEdit={(d) => setModal({ draft: d })} onEmit={admin ? emitirBorrador : null} onDelete={borrarBorrador} canEdit={puedeCrear} />
      )}
      <CNTable rows={pq.items} kind="ventas" partyLabel="Cliente" partyField="cliente_nombre" loading={pq.loading}
        emptyText={pq.vacio} footer={<Pager pq={pq} testid="cn-period" />}
        onView={setViewing} onVoid={puedeAnular ? anular : null} onReceive={puedeCrear ? setReceiving : null} />
      {receiving && <RecepcionModal cn={receiving} onClose={() => setReceiving(null)}
        onDone={() => { setReceiving(null); load(); }} />}
      {modal && <SalesCNModal draft={modal.draft || null} onClose={() => setModal(null)}
        onSaved={() => { setModal(null); load(); }} />}
      {viewing && <CNViewer cn={viewing} onClose={() => setViewing(null)}
        onReload={async () => {
          const { data } = await api.get(`/credit-notes/${viewing.id}`);
          setViewing({ ...data, kind: "ventas" }); load();
        }} />}
    </div>
  );
}

const CONDICIONES = [
  ["disponible", "Disponible para venta"], ["danado", "Dañado · baja por daño"],
  ["vencido", "Vencido · baja por vencimiento"], ["merma", "Merma · baja"],
];

function RecepcionLineas({ filas, recep, setRecepLinea }) {
  return (
    <table className="w-full text-xs" data-testid="cn-dev-recepcion-lineas">
      <thead><tr className="text-left text-[10px] uppercase text-text-muted">
        <th className="p-1.5">Producto</th><th className="p-1.5 text-right">Cant.</th>
        <th className="p-1.5">Condición / destino</th><th className="p-1.5">Lote</th>
      </tr></thead>
      <tbody>
        {filas.map(({ it, key }) => {
          const cfg = recep.lineas[key] || {};
          const lotes = it.lotes || [];
          return (
            <tr key={key} className="border-t border-border/40">
              <td className="p-1.5">{it.descripcion}</td>
              <td className="p-1.5 text-right">{it.cantidad} {it.unidad || ""}</td>
              <td className="p-1.5">
                <select value={cfg.condicion || "disponible"} onChange={(e) => setRecepLinea(key, { condicion: e.target.value })}
                  data-testid={`cn-dev-cond-${key}`} className="bg-app border border-border rounded px-1.5 py-1 text-xs">
                  {CONDICIONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {(cfg.condicion && cfg.condicion !== "disponible") && (
                  <div className="text-[10px] text-agro-yellow mt-0.5">No se suma al inventario disponible para vender</div>)}
              </td>
              <td className="p-1.5">
                <input list={`lotes-${key}`} value={cfg.lote ?? (lotes[0]?.lote || "")} onChange={(e) => setRecepLinea(key, { lote: e.target.value })}
                  placeholder="Lote" className="w-full bg-app border border-border rounded px-1.5 py-1 text-xs" />
                <datalist id={`lotes-${key}`}>{lotes.map((l) => <option key={l.lote} value={l.lote} />)}</datalist>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Confirmar la recepción física de una NC de devolución pendiente (una sola vez). */
export function RecepcionModal({ cn, onClose, onDone }) {
  const [bodegas, setBodegas] = useState([]);
  const [recep, setRecep] = useState({ bodega_id: "", observaciones: "", lineas: {} });
  const [lotes, setLotes] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/warehouses").then(({ data }) => {
      const act = (data || []).filter((b) => b.activo !== false);
      setBodegas(act);
      if (act.length) setRecep((r) => ({ ...r, bodega_id: (act.find((b) => b.tipo === "principal") || act[0]).id }));
    }).catch(() => {});
    if (cn.factura_id) {
      api.get(`/invoices/${cn.factura_id}/returnable-items`).then(({ data }) => {
        const m = {};
        (data.items || []).forEach((r) => { m[r.linea] = r.lotes || []; });
        setLotes(m);
      }).catch(() => {});
    }
  }, [cn]);
  const filas = (cn.items || []).map((it, k) => ({ it: { ...it, lotes: lotes[it.factura_linea] || [] }, key: `n${k}`, k }))
    .filter(({ it }) => it.producto_id);
  const setRecepLinea = (key, patch) => setRecep((r) => ({ ...r, lineas: { ...r.lineas, [key]: { ...(r.lineas[key] || {}), ...patch } } }));
  const confirmar = async () => {
    if (!recep.bodega_id) return toast.error("Selecciona la bodega de recepción");
    setSaving(true);
    try {
      await api.post(`/credit-notes/${cn.id}/recepcion`, {
        bodega_id: recep.bodega_id, observaciones: recep.observaciones,
        lineas: filas.map(({ it, key, k }) => ({ linea: k, condicion: (recep.lineas[key] || {}).condicion || "disponible",
          lote: (recep.lineas[key] || {}).lote ?? ((it.lotes || [])[0]?.lote || "") })),
      });
      toast.success("Recepción registrada: inventario actualizado"); onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const rm = useModalForm({ value: recep, onClose, onSave: confirmar, mensaje: "¿Está seguro de que desea confirmar la recepción de la devolución?" });
  return (
    <Modal title={`Recepción de devolución · ${cn.numero}`} onClose={onClose} m={rm}>
      <div className="text-xs text-text-muted mb-3">Factura {cn.factura_numero || cn.documento_afectado?.numero} · {cn.cliente_nombre}</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Lbl>Bodega de recepción *</Lbl>
          <select value={recep.bodega_id} onChange={(e) => setRecep((r) => ({ ...r, bodega_id: e.target.value }))}
            data-testid="recep-bodega" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">— Selecciona bodega —</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
          </select>
        </div>
        <div>
          <Lbl>Observaciones</Lbl>
          <input value={recep.observaciones} onChange={(e) => setRecep((r) => ({ ...r, observaciones: e.target.value }))}
            className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
        </div>
      </div>
      <RecepcionLineas filas={filas} recep={recep} setRecepLinea={setRecepLinea} />
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={rm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
        <button onClick={rm.guardar} disabled={saving || rm.saving} data-testid="recep-confirmar"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-sm disabled:opacity-50">
          {saving ? "Registrando…" : "Confirmar recepción"}
        </button>
      </div>
    </Modal>
  );
}

export function DevolucionBadge({ cn }) {
  const d = cn.devolucion;
  if (!d || cn.estado === "anulada") return null;
  const pend = d.estado === "pendiente_recepcion";
  return (
    <div className={`mt-1 text-[9px] uppercase px-1.5 py-0.5 rounded border inline-block ${pend ? "border-agro-yellow/40 text-agro-yellow" : "border-agro-green/40 text-agro-green"}`}
      data-testid={`cn-dev-estado-${cn.numero}`}>
      {pend ? "Pendiente de recepción" : `Recibida · ${d.bodega_codigo || ""}`}
    </div>
  );
}

export function SectionHeader({ title, desc, action }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
        {desc && <p className="text-xs text-text-muted mt-0.5 max-w-2xl">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

// Mismo diseño que "Crear proforma": color, tipografía, tamaño, ícono y ubicación
export function CreateButton({ onClick, testid, label, title }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
      className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
      <PlusIcon size={16} weight="bold" /> {label}
    </button>
  );
}

export function SectionToolbar({ q, setQ, estado, setEstado, estados, placeholder, count }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-3 flex gap-3 flex-wrap items-center">
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid="section-search"
          className="w-full bg-app border border-border rounded-md pl-8 pr-3 py-2 text-sm" />
      </div>
      <select value={estado} onChange={(e) => setEstado(e.target.value)} data-testid="section-estado"
        className="bg-app border border-border rounded-md px-3 py-2 text-sm">
        {estados.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <span className="text-[11px] text-text-muted">{count}</span>
    </div>
  );
}

export function DraftsTable({ drafts, refLabel, onEdit, onEmit, onDelete, canEdit, testid }) {
  return (
    <div className="bg-card border border-agro-yellow/30 rounded-lg overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 border-b border-agro-yellow/20 text-[10px] uppercase tracking-wider text-agro-yellow">
        Borradores ({drafts.length}) · sin número fiscal ni efecto en saldos hasta emitirlos
      </div>
      <table className="w-full text-sm">
        <tbody>
          {drafts.map((d) => (
            <tr key={d.id} className="border-t border-border/40" data-testid={`draft-${d.id}`}>
              <td className="p-2.5 pl-4"><span className="text-[10px] uppercase px-2 py-0.5 rounded border border-agro-yellow/40 text-agro-yellow">Borrador</span></td>
              <td className="p-2.5">{d.cliente_nombre}</td>
              <td className="p-2.5 text-xs text-text-muted">{refLabel}: <span className="font-mono">{d.documento_ref || d.ui?.doc_numero || "—"}</span></td>
              <td className="p-2.5 text-xs text-text-secondary max-w-[220px] truncate">{d.motivo}</td>
              <td className="p-2.5 text-right">{fmtL(d.total_estimado)}</td>
              <td className="p-2.5 text-xs text-text-muted">{fmtDate(d.updated_at)}</td>
              <td className="p-2.5 pr-4 text-right whitespace-nowrap">
                {canEdit && <>
                  <button onClick={() => onEdit(d)} className="text-xs text-agro-green hover:underline mr-3" data-testid={`draft-edit-${d.id}`}>Editar</button>
                  {onEmit && <button onClick={() => onEmit(d)} className="text-xs text-agro-green hover:underline mr-3" data-testid={`draft-emit-${d.id}`}>Emitir</button>}
                  <button onClick={() => onDelete(d)} className="text-xs text-text-muted hover:text-red-400" data-testid={`draft-del-${d.id}`}>Eliminar</button>
                </>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** ESTADO del documento (emitida / anulada), común a NOTAS DE DÉBITO y NOTAS DE CRÉDITO. */
export function EstadoSelect({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="section-estado"
      className="bg-app border border-border rounded-md px-3 py-2 text-sm">
      <option value="">TODOS LOS ESTADOS</option>
      <option value="emitida">EMITIDAS</option>
      <option value="anulada">ANULADAS</option>
    </select>
  );
}

export function CNTable({ rows, kind, partyLabel, partyField, onView, onVoid, onReceive, emptyText = "Sin notas de crédito",
                          loading = false, footer = null }) {
  const [imprimir, setImprimir] = useState(null);
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
      {imprimir && <ImprimirNc cn={imprimir} onClose={() => setImprimir(null)} />}
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">Número</th><th className="p-3">Tipo</th>
            <th className="p-3">{kind === "ventas" ? "Documento de origen" : "Factura"}</th><th className="p-3">{partyLabel}</th>
            <th className="p-3">Fecha</th><th className="p-3 text-right">Total</th>
            <th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acc.</th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-text-muted">Cargando…</td></tr>
          : rows.length === 0 ? <tr><td colSpan="8" className="p-12 text-center text-text-muted" data-testid="cn-vacio">{emptyText}</td></tr>
          : rows.map((cn) => (
            <tr key={cn.id} className={`border-t border-border/40 hover:bg-white/[0.02] ${cn.estado === "anulada" ? "opacity-50" : ""}`} data-testid={`cn-row-${cn.numero}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-yellow">{cn.numero}</td>
              <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-app border border-border text-text-secondary" title={cn.tipo_otro || ""}>{cn.tipo === "otro" && cn.tipo_otro ? `otro: ${cn.tipo_otro}` : cn.tipo}</span></td>
              <td className="p-3 font-mono text-xs text-agro-green" data-testid={`cn-doc-${cn.numero}`}>
                {kind === "ventas" && cn.documento_afectado ? (
                  <>
                    <span className="text-[9px] font-sans uppercase text-text-muted mr-1">{cn.documento_afectado.tipo === "nota_debito" ? "ND" : "FAC"}</span>
                    {cn.documento_afectado.numero}
                  </>
                ) : cn.factura_numero}
              </td>
              <td className="p-3 text-sm">{cn[partyField]}</td>
              <td className="p-3 text-xs text-text-secondary">{cn.fecha_emision ? fmtEmision(cn.fecha_emision) : fmtDate(cn.fecha)}</td>
              <td className="p-3 text-right text-sm font-medium">{fmtL(cn.total)}</td>
              <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${cn.estado === "anulada" ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-agro-green/10 text-agro-green border-agro-green/30"}`}>{cn.estado}</span>
                {kind === "ventas" && <DevolucionBadge cn={cn} />}</td>
              <td className="p-3 pr-5 text-right">
                <button aria-label="Ver" onClick={() => onView({ ...cn, kind })} className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`view-cn-${cn.numero}`}><EyeIcon size={16} /></button>
                {kind === "ventas" && (
                  <button onClick={() => setImprimir(cn)} className="p-1.5 text-text-muted hover:text-agro-green" title="Imprimir nota de crédito (incluye la factura de origen)"
                    aria-label="Imprimir nota de crédito" data-testid={`print-cn-${cn.numero}`}><PrinterIcon size={16} /></button>
                )}
                {onReceive && cn.estado !== "anulada" && cn.devolucion?.estado === "pendiente_recepcion" && (
                  <button onClick={() => onReceive(cn)} className="p-1.5 text-agro-yellow hover:text-agro-green" title="Confirmar recepción del producto"
                    data-testid={`receive-cn-${cn.numero}`}><PackageIcon size={16} /></button>)}
                {cn.estado !== "anulada" && onVoid && <button onClick={() => onVoid(cn)} className="p-1.5 text-text-muted hover:text-red-400" title="Anular" aria-label="Anular"><ProhibitIcon size={16} /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  );
}

const RE_NUM_FAC = /^\d{3}-\d{3}-01-\d{8}$/;
const RE_CAI = /^[0-9A-F]{6}(-[0-9A-F]{6}){4}-[0-9A-F]{2}$/;
const MANUAL_VACIO = { numero: "", cai: "", fecha_emision: "", total: "", forma_pago: "credito" };

/** Imprimir / descargar: un solo PDF con la nota de crédito y, después, la factura de origen (solo lectura). */
export function ImprimirNc({ cn, onClose }) {
  return <DocumentoPdfModal url={withTemplate(`/credit-notes/${cn.id}/pdf`, "nota_credito")} titulo="Nota de crédito y factura de origen"
    numero={cn.numero} archivo={`nota-credito-${cn.numero}.pdf`} onClose={onClose} testid="cn-imprimir" />;
}

export function SalesCNModal({ onClose, onSaved, draft = null, traslado = null }) {
  // Borrador: se restaura el formulario tal como se guardó (sin efecto en saldos hasta emitir)
  // traslado: solicitud de traslado a subcuenta NC autorizada → la nota se emite vinculada a ella y a su factura
  const u = draft?.ui || (traslado ? { clientId: traslado.cliente_id, motivo: traslado.motivo, tipo: "ajuste" } : {});
  const draftId = draft?.id || null;
  const keepDraftItems = useRef(Boolean(draft && u.docSel?.tipo === "factura"));
  // "plataforma": la factura está registrada (datos automáticos) · "manual": se digitan sus datos
  const [modo, setModo] = useState(u.modo || "plataforma");
  const [manual, setManual] = useState(u.manual || MANUAL_VACIO);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(u.clientId || "");
  const [facturaId, setFacturaId] = useState(u.docSel?.tipo === "factura" ? u.docSel.id : "");
  const [factura, setFactura] = useState(null);
  // 3.14 — documento afectado elegido con "APLICAR A DOCUMENTO" (factura o ND)
  const [docSel, setDocSel] = useState(u.docSel || null);
  const [tipo, setTipo] = useState(u.tipo || "devolucion");
  const [items, setItems] = useState(u.items || []);
  const [motivo, setMotivo] = useState(u.motivo || "");
  const [tipoOtro, setTipoOtro] = useState(u.tipoOtro || "");
  const [comentario, setComentario] = useState(u.comentario || "");
  // ajuste de precio, descuento, bono u otro: solo el importe (con su ISV) y el motivo de emisión
  const [importe, setImporte] = useState(u.importe ?? (traslado ? traslado.monto : ""));
  const [isvAj, setIsvAj] = useState(u.isvAj ?? 15);
  const [cuenta, setCuenta] = useState(u.cuenta || "");     // filtro de facturas: todas, principal o una subcuenta
  const [fiscal, setFiscal] = useState(null);         // autorización propia de la NC y datos faltantes
  const [resumen, setResumen] = useState(false);      // paso de revisión antes de emitir
  const [vistaPrevia, setVistaPrevia] = useState(null);
  useEffect(() => { api.get("/credit-notes/fiscal-check").then(({ data }) => setFiscal(data)).catch(() => setFiscal(null)); }, []);
  const puedeEmitir = Boolean(fiscal?.puede_emitir);
  const [proyectoId, setProyectoId] = useState(u.proyectoId || "");
  const [proyectos, setProyectos] = useState([]);
  const [actualizaInv] = useState(u.actualizaInv ?? true);
  const [saving, setSaving] = useState(false);
  // Devolución: recepción física (bodega, condición y lote por ítem) y autorización (factura no registrada)
  const [recep, setRecep] = useState(u.recep || { recibir_ahora: true, bodega_id: "", observaciones: "", lineas: {} });
  const [autorizacion, setAutorizacion] = useState(u.autorizacion || "");
  const [bodegas, setBodegas] = useState([]);
  const [productos, setProductos] = useState([]);
  const esDev = tipo === "devolucion";
  useEffect(() => {
    if (!esDev) return;
    api.get("/warehouses").then(({ data }) => {
      const act = (data || []).filter((b) => b.activo !== false);
      setBodegas(act);
      setRecep((r) => (r.bodega_id || !act.length ? r : { ...r, bodega_id: (act.find((b) => b.tipo === "principal") || act[0]).id }));
    }).catch(() => setBodegas([]));
  }, [esDev]);
  useEffect(() => {
    if (!(esDev && modo === "manual")) return;
    api.get("/products").then(({ data }) => setProductos(data || [])).catch(() => setProductos([]));
  }, [esDev, modo]);
  const setRecepLinea = (key, patch) => setRecep((r) => ({ ...r, lineas: { ...r.lineas, [key]: { ...(r.lineas[key] || {}), ...patch } } }));

  useEffect(() => {
    if (!clientId) { setProyectos([]); return; }
    api.get(`/clients/${clientId}/projects`)
      .then(({ data }) => setProyectos((data || []).filter((p) => !p.virtual)))
      .catch(() => setProyectos([]));
  }, [clientId]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, []);
  useEffect(() => {
    if (!traslado) return;
    api.get("/credit-notes/eligible-documents", { params: { cliente_id: traslado.cliente_id, q: traslado.factura_numero } })
      .then(({ data }) => {
        const d = (data || []).find((x) => x.tipo === "factura" && x.id === traslado.factura_id);
        if (d) pickDoc(d); else toast.error("La factura de la solicitud ya no tiene saldo acreditable");
      }).catch((e) => toast.error(apiErr(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traslado?.id]);

  const esND = docSel?.tipo === "nota_debito";
  const limite = !docSel ? 0 : traslado ? Math.min(Number(docSel.saldo_disponible || 0), Number(traslado.monto || 0))
    : Math.max(0, Number(docSel.saldo_disponible || 0) - Number(docSel.en_nc || 0));
  const esManual = modo === "manual";
  const setM = (k, v) => setManual((m) => ({ ...m, [k]: v }));
  const manualErrors = useMemo(() => {
    if (!esManual) return [];
    const e = [];
    if (!RE_NUM_FAC.test(manual.numero.trim())) e.push("Número de factura: formato 000-000-01-00000000");
    if (!RE_CAI.test(manual.cai.trim().toUpperCase())) e.push("CAI: formato XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX");
    if (!manual.fecha_emision) e.push("Fecha de emisión de la factura");
    else if (manual.fecha_emision > new Date().toISOString().slice(0, 10)) e.push("La fecha de emisión no puede ser futura");
    if (!(Number(manual.total) > 0)) e.push("Monto total de la factura");
    return e;
  }, [esManual, manual]);
  const cambiarModo = (m) => {
    setModo(m); setDocSel(null); setFacturaId(""); setFactura(null); setManual(MANUAL_VACIO);
    setItems(m === "manual" ? [{ producto_id: "", codigo: "", descripcion: "", cantidad_factura: 1, cantidad: 1,
      precio_unitario: 0, isv_pct: 15, descuento: 0 }] : []);
    if (m === "manual") setTipo((t) => (t === "devolucion" ? "descuento" : t));
  };

  const pickDoc = (d) => {
    setDocSel(d);
    if (d.tipo === "factura") {
      setFacturaId(d.id);
    } else {
      // NC contra ND: ajuste de valor (sin inventario) por el saldo disponible
      setFacturaId(""); setFactura(null);
      setTipo((t) => (t === "devolucion" ? "ajuste" : t));
      setImporte(d.saldo_disponible); setIsvAj(0);
    }
  };
  const elegirCliente = (cid) => {
    setClientId(cid); setCuenta(""); setFacturaId(""); setDocSel(null); setFactura(null);
    if (!esManual) setItems([]);
  };

  // Ajuste de valor: una sola línea cuyo concepto es el motivo de emisión
  useEffect(() => {
    if (esManual || esDev || !docSel) return;
    const t = Number(isvAj) || 0;
    const m = Number(importe) || 0;
    const base = t ? Math.round((m / (1 + t / 100)) * 100) / 100 : m;
    setItems(m > 0 ? [{ producto_id: "", codigo: "AJUSTE", descripcion: motivo.trim() || "Ajuste de valor", cantidad_factura: 1, cantidad: 1,
      precio_unitario: base, isv_pct: t, descuento: 0 }] : []);
  }, [importe, isvAj, motivo, esDev, esManual, docSel]);

  useEffect(() => {
    if (!facturaId) { setFactura(null); if (!esND && modo !== "manual") setItems([]); return; }
    (async () => {
      const { data } = await api.get(`/invoices/${facturaId}`);
      setFactura(data);
      if (keepDraftItems.current) { keepDraftItems.current = false; return; }   // borrador: conserva sus líneas
      setProyectoId(data.proyecto_id || "");
      if (esDev) {
        // devolución: cantidad facturada, devuelta anteriormente y disponible por línea
        const { data: dv } = await api.get(`/invoices/${facturaId}/returnable-items`);
        setItems((dv.items || []).map((r) => ({
          producto_id: r.producto_id, codigo: r.codigo, descripcion: r.descripcion, unidad: r.unidad,
          factura_linea: r.linea, cantidad_factura: r.cantidad_facturada, cantidad_devuelta: r.cantidad_devuelta,
          cantidad_disponible: r.cantidad_disponible, precio_unitario: r.precio_unitario, isv_pct: r.isv_pct,
          descuento_unitario: r.descuento_unitario, lotes: r.lotes || [],
          // tal como están en la factura: todo lo disponible marcado para devolver (se puede reducir o desmarcar)
          cantidad: r.cantidad_disponible, sel: Number(r.cantidad_disponible) > 0,
          descuento: Math.round(Number(r.descuento_unitario || 0) * Number(r.cantidad_disponible || 0) * 100) / 100,
        })));
        return;
      }
      if (!draftId) setIsvAj(Number(data.subtotal_gravado_15) > 0 ? 15 : Number(data.subtotal_gravado_18) > 0 ? 18 : 0);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaId, esDev]);

  const totals = useMemo(() => {
    let s15 = 0, s18 = 0, sx = 0, i15 = 0, i18 = 0, total = 0;
    const exo = factura && factura.subtotal_exonerado > 0;
    items.forEach((it) => {
      if (esDev && modo !== "manual" && !it.sel) return;
      const neto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) - (Number(it.descuento) || 0);
      if (neto <= 0) return;
      if (exo) { total += neto; }
      else if (Number(it.isv_pct) === 15) { s15 += neto; i15 += neto * 0.15; total += neto * 1.15; }
      else if (Number(it.isv_pct) === 18) { s18 += neto; i18 += neto * 0.18; total += neto * 1.18; }
      else { sx += neto; total += neto; }
    });
    return { s15, s18, sx, i15, i18, total };
  }, [items, factura, esDev, modo]);

  // Clave estable de cada línea (para condición / lote de la recepción)
  const keyOf = (it, i) => (esManual ? `m${i}` : `f${it.factura_linea ?? i}`);
  const filasManual = () => items.map((it, i) => ({ it, key: keyOf(it, i) }))
    .filter(({ it }) => Number(it.cantidad) > 0 && Number(it.precio_unitario) > 0);
  const filasPlataforma = () => items.map((it, i) => ({ it, key: keyOf(it, i) }))
    .filter(({ it }) => Number(it.cantidad) > 0 && (!esDev || it.sel));
  const itemsManual = () => filasManual().map(({ it }) => ({
    producto_id: esDev ? (it.producto_id || "") : "", codigo: it.codigo || "AJUSTE", descripcion: it.descripcion || motivo,
    cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario), descuento: 0, isv_pct: Number(it.isv_pct),
  }));
  const itemsPlataforma = () => filasPlataforma().map(({ it }) => ({
    producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion,
    cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario),
    descuento: Number(it.descuento) || 0, isv_pct: Number(it.isv_pct),
    ...(esDev ? { factura_linea: it.factura_linea } : {}),
  }));
  // recepción: una línea por ítem devuelto, en el mismo orden que los ítems enviados
  const recepcionPayload = () => {
    if (!esDev) return null;
    const filas = esManual ? filasManual() : filasPlataforma();
    return {
      recibir_ahora: Boolean(recep.recibir_ahora), bodega_id: recep.bodega_id || "", observaciones: recep.observaciones || "",
      lineas: filas.map(({ it, key }, k) => ({
        linea: k, condicion: (recep.lineas[key] || {}).condicion || "disponible",
        lote: (recep.lineas[key] || {}).lote ?? ((it.lotes || [])[0]?.lote || ""),
      })),
    };
  };
  const buildPayload = () => (esManual ? {
    documento_tipo: "factura_manual", cliente_id: clientId, tipo, tipo_otro: tipo === "otro" ? tipoOtro : "",
    items: itemsManual(), motivo, comentario, actualiza_inventario: esDev, proyecto_id: proyectoId || null,
    documento_manual: { ...manual, cai: (manual.cai || "").trim().toUpperCase(), numero: (manual.numero || "").trim(),
                        total: Number(manual.total) || 0 },
    recepcion: recepcionPayload(), autorizacion,
  } : {
    documento_tipo: docSel?.tipo || "factura", documento_id: docSel?.id || null, cliente_id: clientId,
    factura_id: docSel?.tipo === "factura" ? docSel.id : null,
    tipo, tipo_otro: tipo === "otro" ? tipoOtro : "",
    items: itemsPlataforma(), motivo, comentario, proyecto_id: proyectoId || null,
    actualiza_inventario: !esND && esDev, recepcion: recepcionPayload(),
    ...(traslado ? { traslado_id: traslado.id } : {}),
  });
  const uiState = () => ({ modo, manual, clientId, docSel, tipo, items, motivo, comentario, tipoOtro, proyectoId, actualizaInv, importe, isvAj, cuenta,
                           recep, autorizacion,
                           doc_numero: esManual ? manual.numero : (docSel?.numero || "") });
  // Emite: directo, o (si viene de un borrador) actualiza el borrador y lo emite una sola vez
  const emitir = async (payload) => {
    if (draftId) {
      await api.post("/credit-notes-drafts", { id: draftId, payload, ui: uiState() });
      return api.post(`/credit-notes-drafts/${draftId}/emit`);
    }
    return api.post("/credit-notes", payload);
  };
  const guardarBorrador = async () => {
    if (!clientId) return toast.error("Selecciona el cliente");
    setSaving(true);
    try {
      await api.post("/credit-notes-drafts", { id: draftId, payload: buildPayload(), ui: uiState() });
      toast.success("Borrador guardado (sin efecto en saldos)"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const saveManual = async (soloValidar) => {
    if (!clientId) return toast.error("Selecciona el cliente de la factura");
    if (manualErrors.length) return toast.error(manualErrors[0]);
    if (itemsManual().length === 0) return toast.error("Agrega al menos una línea con cantidad y precio");
    if (esDev) {
      if (itemsManual().some((it) => !it.producto_id)) return toast.error("Selecciona el producto del catálogo en cada línea devuelta");
      if (!autorizacion.trim()) return toast.error("Indica la autorización / justificación de la devolución");
      if (recep.recibir_ahora && !recep.bodega_id) return toast.error("Selecciona la bodega de recepción");
    }
    if (totals.total > Number(manual.total) + 0.01) return toast.error("El total de la nota excede el monto de la factura");
    if (!motivo.trim()) return toast.error("El motivo de emisión es obligatorio");
    if (tipo === "otro" && !tipoOtro.trim()) return toast.error("Describe el tipo de nota de crédito");
    if (soloValidar === true) return true;
    setSaving(true);
    try {
      const { data } = await emitir(buildPayload());
      toast.success(`Nota de crédito ${data.numero} emitida · saldo a favor del cliente ${fmtL(data.saldo_a_favor_generado || 0)}`);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const save = async (soloValidar) => {
    if (esManual) return saveManual(soloValidar);
    if (!docSel) return toast.error("Aplica la nota a un documento (factura o nota de débito)");
    if (totals.total > limite + 0.01)
      return toast.error(traslado ? `El total excede el importe autorizado en la solicitud (${fmtL(limite)})`
        : Number(docSel.en_nc || 0) > 0.004 ? `El total excede lo acreditable: ${fmtL(docSel.en_nc)} de la factura está en la subcuenta NC, reservado para su solicitud (máximo ${fmtL(limite)})`
          : `El total excede lo acreditable del documento (${fmtL(limite)})`);
    if (esDev) {
      const sel = items.filter((it) => it.sel);
      if (!sel.length) return toast.error("Selecciona al menos un ítem devuelto");
      const mal = sel.find((it) => !(Number(it.cantidad) > 0) || Number(it.cantidad) > Number(it.cantidad_disponible) + 1e-9);
      if (mal) return toast.error(`${mal.descripcion}: la cantidad debe ser mayor que 0 y no superar ${mal.cantidad_disponible}`);
      if (recep.recibir_ahora && !recep.bodega_id) return toast.error("Selecciona la bodega de recepción");
    }
    if (itemsPlataforma().length === 0) return toast.error(esDev ? "Indica cantidades a devolver" : "Indica el importe a acreditar");
    if (!motivo.trim()) return toast.error("El motivo de emisión es obligatorio");
    if (tipo === "otro" && !tipoOtro.trim()) return toast.error("Describe el tipo de nota de crédito");
    if (soloValidar === true) return true;
    setSaving(true);
    try {
      const { data } = await emitir(buildPayload());
      toast.success(`Nota de crédito ${data.numero} emitida` +
        (Number(data.saldo_a_favor_generado) > 0 ? ` · saldo a favor ${fmtL(data.saldo_a_favor_generado)}` : ""));
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const cm = useModalForm({ value: { modo, manual, clientId, docSel, tipo, items, motivo, comentario, tipoOtro, proyectoId, recep, autorizacion, importe, isvAj },
                           onClose, onSave: () => save(), mensaje: "¿Está seguro de que desea emitir la nota de crédito?" });
  const revisar = async () => {
    if (fiscal && !fiscal.ok) return toast.error(fiscal.faltantes[0] || "Falta la configuración fiscal de la nota de crédito");
    if ((await save(true)) === true) setResumen(true);
  };
  const cdm = useModalForm({ abierto: false, onSave: guardarBorrador, mensaje: "¿Está seguro de que desea guardar el borrador?" });
  return (
    <Modal title={draftId ? "Borrador de nota de crédito" : "Crear nota de crédito"} onClose={onClose} m={cm}>
      {vistaPrevia && <DocumentoPdfModal url="/credit-notes/preview" body={vistaPrevia} titulo="Vista previa · sin valor fiscal" numero="Nota de crédito"
        archivo="nota-credito-vista-previa.pdf" onClose={() => setVistaPrevia(null)} testid="cn-vista-previa" />}
      {resumen ? <ResumenEmision docSel={docSel} manual={esManual ? manual : null} tipo={tipo} tipoOtro={tipoOtro} motivo={motivo} comentario={comentario}
        filas={(esManual ? filasManual() : filasPlataforma()).map(({ it }) => it)} totals={totals} esDev={esDev} recep={recep} bodegas={bodegas}
        fiscal={fiscal} traslado={traslado} saving={saving || cm.saving}
        onVolver={() => setResumen(false)} onVistaPrevia={() => setVistaPrevia(buildPayload())} onEmitir={cm.guardar} /> : <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* 1) Cliente primero — filtra facturas */}
        <div className="col-span-full">
          <Lbl>Cliente *</Lbl>
          <ClientePicker clients={clients} value={clientId} onChange={elegirCliente} disabled={Boolean(traslado)} />
        </div>
        {clientId && proyectos.length > 0 && (
          <div className="col-span-full">
            <Lbl>Cuenta o subcuenta</Lbl>
            <CuentaSelect proyectos={proyectos} value={cuenta} disabled={Boolean(traslado)}
              onChange={(v) => { setCuenta(v); if (v && v !== "principal") setProyectoId(v); if (v === "principal") setProyectoId("");
                if (docSel && !traslado) { setDocSel(null); setFacturaId(""); setFactura(null); } }} />
            <div className="text-[10px] text-text-muted mt-1">Muestra las facturas de esa cuenta. La nota se descuenta del saldo de la cuenta de la factura.</div>
          </div>
        )}

        {/* 2) ¿La factura está registrada en la plataforma? */}
        {!traslado && <div className="col-span-full">
          <Lbl>Factura a la que se aplica *</Lbl>
          <div className="grid grid-cols-2 gap-2" data-testid="cn-modo">
            {[["plataforma", "Registrada en la plataforma", "Se selecciona y sus datos (número, CAI, fecha, cliente) se completan solos."],
              ["manual", "No registrada · ingreso manual", "Factura emitida fuera del sistema: se digitan y validan sus datos."]].map(([k, t, d]) => (
              <button key={k} type="button" onClick={() => cambiarModo(k)} data-testid={`cn-modo-${k}`}
                className={`text-left rounded-md border px-3 py-2 ${modo === k ? "border-agro-green bg-agro-green/10" : "border-border hover:border-agro-green/40"}`}>
                <div className={`text-xs font-semibold ${modo === k ? "text-agro-green" : ""}`}>{t}</div>
                <div className="text-[10px] text-text-muted mt-0.5">{d}</div>
              </button>
            ))}
          </div>
        </div>}

        {traslado && (
          <div className="col-span-full text-xs bg-blue-500/5 border border-blue-500/30 rounded-md p-3" data-testid="cn-desde-traslado">
            Emisión de la solicitud de traslado a subcuenta NC de la factura <span className="font-mono">{traslado.factura_numero}</span>,
            autorizada por {fmtL(traslado.monto)}. La nota queda vinculada a la factura y a la solicitud; lo que no se acredite regresa a su cuenta de origen.
          </div>
        )}

        {esManual && (
          <div className="col-span-full bg-agro-yellow/5 border border-agro-yellow/40 rounded-lg p-3" data-testid="cn-manual-form">
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow font-semibold mb-2">
              Datos de la factura (ingreso manual)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="md:col-span-2">
                <Lbl>Número de factura *</Lbl>
                <input value={manual.numero} onChange={(e) => setM("numero", e.target.value)} placeholder="000-001-01-00000000"
                  data-testid="cn-man-numero" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs font-mono" />
              </div>
              <div className="md:col-span-3">
                <Lbl>CAI con que se emitió *</Lbl>
                <input value={manual.cai} onChange={(e) => setM("cai", e.target.value.toUpperCase())} placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX"
                  data-testid="cn-man-cai" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs font-mono" />
              </div>
              <div>
                <Lbl>Fecha de emisión *</Lbl>
                <input type="date" value={manual.fecha_emision} onChange={(e) => setM("fecha_emision", e.target.value)}
                  data-testid="cn-man-fecha" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
              </div>
              <div>
                <Lbl>Monto total *</Lbl>
                <input type="number" min={0} step="0.01" value={manual.total} onChange={(e) => setM("total", e.target.value)}
                  data-testid="cn-man-total" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs text-right" />
              </div>
              <div>
                <Lbl>Forma de pago (referencia)</Lbl>
                <select value={manual.forma_pago} onChange={(e) => setM("forma_pago", e.target.value)}
                  data-testid="cn-man-forma" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                  <option value="credito">Crédito</option>
                  <option value="contado">Contado</option>
                </select>
              </div>
            </div>
            {manualErrors.length > 0 && (manual.numero || manual.cai) && (
              <ul className="mt-2 text-[11px] text-red-300 list-disc pl-4" data-testid="cn-man-errors">
                {manualErrors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* 3.14 — APLICAR A DOCUMENTO (factura o nota de débito del cliente) */}
        {!esManual && !traslado && <div className="col-span-full">
          <Lbl>Factura de origen *</Lbl>
          {docSel ? (
            <div className="flex items-center justify-between gap-2 text-xs" data-testid="cn-doc-selected">
              <span>{docSel.tipo === "factura" ? "Factura" : "Nota de Débito"} <span className="font-mono text-agro-green">{docSel.numero}</span></span>
              <button type="button" onClick={() => { setDocSel(null); setFacturaId(""); setFactura(null); setItems([]); }} data-testid="cn-doc-cambiar"
                className="text-agro-green min-h-[36px] px-2">Cambiar factura</button>
            </div>
          ) : <FacturasCliente clienteId={clientId} cuenta={cuenta} seleccion={docSel?.id} onPick={pickDoc} />}
        </div>}

        {/* 3) Datos del comprobante afectado (arrastrados del documento, no digitados) */}
        {docSel && (
          <div className="col-span-full bg-agro-green/5 border border-agro-green/30 rounded-lg p-3" data-testid="cn-comprobante-info">
            <div className="text-[10px] uppercase tracking-wider text-agro-green font-semibold mb-2">
              Referencia · comprobante de origen ({docSel.tipo === "factura" ? "Factura" : "Nota de Débito"}) · datos de su emisión, no de la nota
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-text-muted text-[10px] uppercase mb-0.5">Nº Correlativo</div>
                <div className="font-mono text-text-primary" data-testid="cn-inv-numero">{docSel.numero}</div>
              </div>
              <div>
                <div className="text-text-muted text-[10px] uppercase mb-0.5">Fecha de emisión</div>
                <div className="font-mono text-text-primary" data-testid="cn-inv-fecha">{fmtDate(docSel.fecha_emision)}</div>
              </div>
              <div className="col-span-full">
                <div className="text-text-muted text-[10px] uppercase mb-0.5">CAI</div>
                <div className="font-mono text-text-primary text-[11px] truncate" title={docSel.cai} data-testid="cn-inv-cai">
                  {docSel.cai || "—"}
                </div>
              </div>
              <div className="col-span-full">
                <div className="text-text-muted text-[10px] uppercase mb-0.5">Cliente</div>
                <div className="text-text-primary">{factura?.cliente_nombre || clients.find((c) => c.id === clientId)?.nombre || ""}</div>
              </div>
              <div>
                <div className="text-text-muted text-[10px] uppercase mb-0.5">Monto original</div>
                <div className="font-mono text-agro-yellow">{fmtL(docSel.monto_original)}</div>
              </div>
              <div>
                <div className="text-text-muted text-[10px] uppercase mb-0.5">{traslado ? "Autorizado en la solicitud" : "Disponible para acreditar"}</div>
                <div className="font-mono text-agro-green" data-testid="cn-doc-saldo">{fmtL(limite)}</div>
                {!traslado && Number(docSel.en_nc || 0) > 0.004 && <div className="text-[10px] text-text-muted">{fmtL(docSel.en_nc)} en subcuenta NC (reservado)</div>}
              </div>
            </div>
          </div>
        )}

        {/* Datos propios de la nota (autorización fiscal de la NC, nunca los de la factura) */}
        <div className="col-span-full bg-app border border-border rounded-lg p-3" data-testid="cn-datos-propios">
          <div className="text-[10px] uppercase tracking-wider text-agro-yellow font-semibold mb-2">Datos propios de la nota de crédito</div>
          {!fiscal ? <div className="text-xs text-text-muted">Verificando la autorización fiscal…</div> : !fiscal.ok ? (
            <div className="text-xs text-red-300 flex gap-1.5" data-testid="cn-fiscal-faltantes"><WarningIcon size={14} className="flex-shrink-0 mt-0.5" />
              <div>No se puede emitir: {fiscal.faltantes.join(" · ")}</div></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><div className="text-text-muted text-[10px] uppercase mb-0.5">Número</div><div>Se asigna al emitir</div></div>
              <div><div className="text-text-muted text-[10px] uppercase mb-0.5">Fecha de emisión</div><div>{fmtDate(new Date().toISOString())}</div></div>
              <div className="col-span-full"><div className="text-text-muted text-[10px] uppercase mb-0.5">CAI de la nota de crédito</div>
                <div className="font-mono text-[11px] break-all" data-testid="cn-cai-propio">{fiscal.cai?.numero}</div></div>
              <div className="col-span-full"><div className="text-text-muted text-[10px] uppercase mb-0.5">Rango autorizado</div>
                <div className="font-mono text-[11px]">{fiscal.cai?.rango_inicial} a {fiscal.cai?.rango_final}</div></div>
              <div><div className="text-text-muted text-[10px] uppercase mb-0.5">Fecha límite de emisión</div><div>{fiscal.cai?.fecha_limite ? fmtDate(fiscal.cai.fecha_limite) : "—"}</div></div>
              <div><div className="text-text-muted text-[10px] uppercase mb-0.5">Números disponibles</div><div>{fiscal.cai?.restantes}</div></div>
            </div>
          )}
          {(fiscal?.avisos || []).map((a) => <div key={a} className="mt-2 text-[11px] text-agro-yellow flex gap-1.5" data-testid="cn-fiscal-aviso"><WarningIcon size={13} className="flex-shrink-0 mt-0.5" />{a}</div>)}
        </div>

        <div>
          <Lbl>Tipo *</Lbl>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="cn-tipo" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="devolucion" disabled={esND}>Devolución de productos</option>
            <option value="descuento">Descuento posterior</option>
            <option value="ajuste">Ajuste de precio</option>
            <option value="bono">Bono / Bonificación</option>
            <option value="otro">Otro (especificar)</option>
          </select>
          {tipo === "otro" && (
            <input value={tipoOtro} onChange={(e) => setTipoOtro(e.target.value)} placeholder="Describe el tipo *"
              data-testid="cn-tipo-otro" className="mt-2 w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
          )}
        </div>
        <div>
          <Lbl>Motivo de emisión *</Lbl>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} placeholder="Razón de la nota (obligatorio, se imprime)…" data-testid="cn-motivo" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="col-span-full">
          <Lbl>Comentario (se imprime en la nota)</Lbl>
          <textarea rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} maxLength={1000} data-testid="cn-comentario"
            placeholder="Explicación adicional para el cliente (opcional)…" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        {esManual && proyectos.length > 0 && (
          <div className="col-span-full">
            <Lbl>Proyecto / subcuenta</Lbl>
            <select value={proyectoId} onChange={(e) => setProyectoId(e.target.value)} data-testid="cn-proyecto"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— Cuenta principal —</option>
              {proyectos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
            </select>
          </div>
        )}
      </div>

      {factura && (
        <div className="bg-app border border-border rounded-md p-3 mb-3 text-xs text-text-secondary">
          <span className="text-text-muted">Cliente:</span> <span className="text-text-primary">{factura.cliente_nombre}</span> · 
          <span className="text-text-muted ml-2">Forma pago:</span> <span className="capitalize">{factura.forma_pago}</span> · 
          <span className="text-text-muted ml-2">Saldo:</span> <span className="text-agro-yellow">{fmtL(factura.saldo_pendiente || 0)}</span> · 
          <span className="text-text-muted ml-2">Total:</span> <span className="text-agro-green">{fmtL(factura.total)}</span>
        </div>
      )}

      {esManual && (
        <div className="border border-border rounded-md overflow-hidden mb-4" data-testid="cn-man-lines">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2 pl-4">Descripción</th><th className="p-2 text-right w-24">Cantidad</th>
                <th className="p-2 text-right w-32">Precio</th><th className="p-2 text-right w-24">ISV</th>
                <th className="p-2 text-right w-32">Subtotal</th><th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-border/40">
                  <td className="p-2 pl-4">{esDev ? (
                    <select value={it.producto_id || ""} data-testid={`cn-man-prod-${i}`}
                      onChange={(e) => {
                        const pr = productos.find((x) => x.id === e.target.value) || {};
                        const n = [...items];
                        n[i] = { ...n[i], producto_id: pr.id || "", codigo: pr.codigo || "", descripcion: pr.nombre || "",
                                 isv_pct: pr.isv ?? n[i].isv_pct, unidad: pr.unidad || "" };
                        setItems(n);
                      }}
                      className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs">
                      <option value="">— Producto del catálogo —</option>
                      {productos.map((pr) => <option key={pr.id} value={pr.id}>{pr.codigo} · {pr.nombre}</option>)}
                    </select>
                  ) : (
                    <input value={it.descripcion} placeholder="Concepto de la nota" data-testid={`cn-man-desc-${i}`}
                      onChange={(e) => { const n = [...items]; n[i] = { ...n[i], descripcion: e.target.value }; setItems(n); }}
                      className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" />
                  )}</td>
                  <td className="p-2"><input type="number" min={0} step="0.01" value={it.cantidad}
                    onChange={(e) => { const n = [...items]; n[i] = { ...n[i], cantidad: e.target.value }; setItems(n); }}
                    className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                  <td className="p-2"><input type="number" min={0} step="0.01" value={it.precio_unitario} data-testid={`cn-man-precio-${i}`}
                    onChange={(e) => { const n = [...items]; n[i] = { ...n[i], precio_unitario: e.target.value }; setItems(n); }}
                    className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                  <td className="p-2"><select value={it.isv_pct}
                    onChange={(e) => { const n = [...items]; n[i] = { ...n[i], isv_pct: Number(e.target.value) }; setItems(n); }}
                    className="w-full bg-app border border-border rounded px-1 py-1.5 text-xs">
                    <option value={15}>15%</option><option value={18}>18%</option><option value={0}>Exento</option></select></td>
                  <td className="p-2 text-right text-xs">{fmtL((Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0))}</td>
                  <td className="p-2">{items.length > 1 && (
                    <button aria-label="Cerrar" type="button" onClick={() => setItems(items.filter((_, k) => k !== i))} className="text-text-muted hover:text-red-300"><XIcon size={14} /></button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={() => setItems([...items, { producto_id: "", codigo: "", descripcion: "", cantidad_factura: 1, cantidad: 1, precio_unitario: 0, isv_pct: 15, descuento: 0 }])}
            className="m-2 text-xs text-agro-green flex items-center gap-1"><PlusIcon size={12} /> Agregar línea</button>
        </div>
      )}

      {!esManual && esDev && items.length > 0 && (
        <div className="border border-border rounded-md overflow-x-auto mb-4" data-testid="cn-dev-items">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted bg-sidebar">
            Ítems devueltos · precio, descuento e impuesto originales de la factura
          </div>
          <table className="w-full text-xs">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2 pl-3 w-8"></th><th className="p-2">Producto</th>
                <th className="p-2 text-right">Facturado</th><th className="p-2 text-right">Devuelto antes</th>
                <th className="p-2 text-right">Disponible</th><th className="p-2 text-right w-28">A devolver</th>
                <th className="p-2 text-right">Precio</th><th className="p-2 text-right">Desc.</th>
                <th className="p-2 text-right">ISV</th><th className="p-2 pr-3 text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const agotado = Number(it.cantidad_disponible) <= 0;
                const neto = (Number(it.cantidad) || 0) * Number(it.precio_unitario) - (Number(it.descuento) || 0);
                const setCant = (v) => {
                  const n = [...items];
                  const q = Number(v) || 0;
                  n[i] = { ...n[i], cantidad: v, descuento: Math.round(Number(it.descuento_unitario || 0) * q * 100) / 100, sel: q > 0 || n[i].sel };
                  setItems(n);
                };
                return (
                  <tr key={i} className={`border-t border-border/40 ${agotado ? "opacity-50" : ""}`} data-testid={`cn-dev-row-${i}`}>
                    <td className="p-2 pl-3"><input type="checkbox" disabled={agotado} checked={Boolean(it.sel)} data-testid={`cn-dev-sel-${i}`}
                      onChange={(e) => { const n = [...items]; n[i] = { ...n[i], sel: e.target.checked }; setItems(n); }}
                      className="w-4 h-4 accent-agro-green" /></td>
                    <td className="p-2"><div className="font-mono text-agro-green">{it.codigo}</div><div className="text-text-secondary">{it.descripcion}</div></td>
                    <td className="p-2 text-right">{it.cantidad_factura} {it.unidad}</td>
                    <td className="p-2 text-right text-text-muted">{it.cantidad_devuelta}</td>
                    <td className="p-2 text-right font-semibold">{it.cantidad_disponible}</td>
                    <td className="p-2"><input type="number" min={0} max={it.cantidad_disponible} step="0.01" disabled={agotado}
                      value={it.cantidad} onChange={(e) => setCant(e.target.value)} data-testid={`cn-dev-qty-${i}`}
                      className={`w-full bg-app border rounded px-2 py-1 text-right ${Number(it.cantidad) > Number(it.cantidad_disponible) ? "border-red-500" : "border-border"}`} /></td>
                    <td className="p-2 text-right">{fmtL(it.precio_unitario)}</td>
                    <td className="p-2 text-right">{fmtL(it.descuento || 0)}</td>
                    <td className="p-2 text-right">{Number(it.isv_pct) ? `${it.isv_pct}%` : "Exento"}</td>
                    <td className="p-2 pr-3 text-right font-medium">{fmtL(Math.max(neto, 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!esManual && !esDev && docSel && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-testid="cn-ajuste">
          <div>
            <Lbl>Importe a acreditar (incluye ISV) *</Lbl>
            <input type="number" min={0} step="0.01" value={importe} onChange={(e) => setImporte(e.target.value)} data-testid="cn-importe"
              className="w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm text-right" />
            <div className="text-[10px] text-text-muted mt-1">Máximo {fmtL(limite)}. El concepto de la nota es el motivo de emisión; no mueve inventario.</div>
          </div>
          <div>
            <Lbl>ISV</Lbl>
            <select value={isvAj} onChange={(e) => setIsvAj(Number(e.target.value))} data-testid="cn-isv"
              className="w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm">
              <option value={15}>Incluye ISV 15 %</option><option value={18}>Incluye ISV 18 %</option><option value={0}>Exento (sin ISV)</option>
            </select>
          </div>
        </div>
      )}

      {esDev && esManual && (
        <div className="mb-3 bg-agro-yellow/5 border border-agro-yellow/40 rounded-md p-3 text-xs" data-testid="cn-dev-autorizacion">
          <div className="text-agro-yellow font-semibold mb-1">Devolución sin factura del sistema</div>
          <div className="text-text-muted mb-2">No se puede validar la cantidad contra una factura registrada: requiere autorización de
            administración o contabilidad y queda registrada con la nota.</div>
          <textarea rows={2} value={autorizacion} onChange={(e) => setAutorizacion(e.target.value)} data-testid="cn-dev-autorizacion-txt"
            placeholder="Autorización / justificación (quién autoriza y por qué)…"
            className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
        </div>
      )}

      {esDev && (esManual ? filasManual() : filasPlataforma()).length > 0 && (
        <div className="mb-4 border border-agro-green/30 rounded-md p-3" data-testid="cn-dev-recepcion">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={Boolean(recep.recibir_ahora)} data-testid="cn-dev-recibir-ahora"
              onChange={(e) => setRecep((r) => ({ ...r, recibir_ahora: e.target.checked }))} className="w-4 h-4 accent-agro-green" />
            Registrar ahora la recepción física del producto
          </label>
          <div className="text-[11px] text-text-muted mt-1 mb-2">
            {recep.recibir_ahora
              ? "El reingreso al inventario se registra junto con la nota."
              : "La nota queda pendiente de recepción; el inventario se actualiza al confirmar la recepción."}
          </div>
          {recep.recibir_ahora && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div>
                  <Lbl>Bodega de recepción *</Lbl>
                  <select value={recep.bodega_id} onChange={(e) => setRecep((r) => ({ ...r, bodega_id: e.target.value }))}
                    data-testid="cn-dev-bodega" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs">
                    <option value="">— Selecciona bodega —</option>
                    {bodegas.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <Lbl>Observaciones</Lbl>
                  <input value={recep.observaciones || ""} onChange={(e) => setRecep((r) => ({ ...r, observaciones: e.target.value }))}
                    className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
                </div>
              </div>
              <RecepcionLineas filas={esManual ? filasManual() : filasPlataforma()} recep={recep} setRecepLinea={setRecepLinea} />
            </>
          )}
        </div>
      )}

      <div className="bg-card border border-agro-yellow/30 rounded-md p-4 mb-4 grid grid-cols-2 gap-2 text-sm">
        <Row l="Gravado 15%" v={fmtL(totals.s15)} />
        <Row l="ISV 15%" v={fmtL(totals.i15)} />
        <Row l="Gravado 18%" v={fmtL(totals.s18)} />
        <Row l="ISV 18%" v={fmtL(totals.i18)} />
        <Row l="Exento" v={fmtL(totals.sx)} />
        <div></div>
        <div className="col-span-full border-t border-border pt-2 flex justify-between items-baseline">
          <span className="text-text-secondary text-xs uppercase tracking-wider">Total NC</span>
          <span className="font-heading text-2xl font-semibold text-agro-yellow" data-testid="cn-total">{fmtL(totals.total)}</span>
        </div>
      </div>

      {(() => {
        const pendiente = esManual ? 0 : Number(docSel?.saldo_pendiente ?? 0);
        const favor = esManual ? totals.total : Math.max(0, totals.total - pendiente);
        if (totals.total <= 0 || (!esManual && !docSel) || favor <= 0.004) return null;
        return (
          <div className="mb-3 text-xs bg-agro-yellow/5 border border-agro-yellow/40 rounded-md p-3" data-testid="cn-favor-hint">
            {esManual
              ? <>Factura no registrada: el crédito de <b>{fmtL(favor)}</b> quedará como <b>saldo a favor</b> del cliente, aplicable a facturas futuras.</>
              : <>El documento tiene saldo pendiente de {fmtL(pendiente)}: <b>{fmtL(favor)}</b> quedará como <b>saldo a favor</b> del cliente.</>}
          </div>
        );
      })()}

      {fiscal && !puedeEmitir && (
        <div className="mb-3 text-xs text-text-secondary" data-testid="cn-solo-borrador">
          La emisión la autoriza el Administrador General: guarda la nota como borrador para su revisión.</div>
      )}
      <div className="flex flex-wrap justify-end gap-3">
        <button onClick={cm.cerrar} className="min-h-[44px] px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
        {!traslado && <button onClick={cdm.guardar} disabled={saving || cm.saving || cdm.saving || !clientId} data-testid="cn-save-draft"
          className="min-h-[44px] px-4 py-2 text-sm border border-agro-green/50 text-agro-green rounded-md hover:bg-agro-green/10 disabled:opacity-40">
          Guardar borrador
        </button>}
        {puedeEmitir && <button onClick={revisar} disabled={saving || cm.saving || cdm.saving || totals.total <= 0 || !motivo.trim()} data-testid="cn-submit"
          className="min-h-[44px] bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-sm disabled:opacity-50">Revisar y emitir</button>}
      </div>
      </>}
    </Modal>
  );
}

const TIPO_LBL = { devolucion: "Devolución de productos", descuento: "Descuento posterior", ajuste: "Ajuste de precio", bono: "Bono / Bonificación", otro: "Otro" };

/** Paso de revisión antes de emitir: referencia de la factura, datos propios de la nota, detalle, totales y efectos. */
function ResumenEmision({ docSel, manual, tipo, tipoOtro, motivo, comentario, filas, totals, esDev, recep, bodegas, fiscal, traslado, saving,
                          onVolver, onVistaPrevia, onEmitir }) {
  const ref = manual ? { numero: manual.numero, cai: manual.cai, fecha_emision: manual.fecha_emision } : (docSel || {});
  const bodega = bodegas.find((b) => b.id === recep.bodega_id);
  const unidades = filas.reduce((s, it) => s + (Number(it.cantidad) || 0), 0);
  return (
    <div className="space-y-4 text-sm" data-testid="cn-resumen">
      <div className="text-xs text-text-secondary">Revisa la nota antes de emitirla. Al confirmar se asignan su número y su CAI y se aplica su efecto una sola vez.</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-agro-green/5 border border-agro-green/30 rounded-md p-3 text-xs space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-agro-green font-semibold">Comprobante de origen (referencia)</div>
          <div>Factura <span className="font-mono">{ref.numero}</span></div>
          <div>CAI <span className="font-mono break-all">{ref.cai || "—"}</span></div>
          <div>Emitida el {fmtDate(ref.fecha_emision)}</div>
        </div>
        <div className="bg-app border border-border rounded-md p-3 text-xs space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-agro-yellow font-semibold">Nota de crédito</div>
          <div>{TIPO_LBL[tipo]}{tipo === "otro" && tipoOtro ? ` · ${tipoOtro}` : ""}</div>
          <div>CAI propio <span className="font-mono break-all">{fiscal?.cai?.numero || "—"}</span></div>
          <div>Número: se asigna al emitir · fecha {fmtDate(new Date().toISOString())}</div>
          {traslado && <div>Solicitud de traslado · {fmtL(traslado.monto)} autorizado</div>}
        </div>
      </div>
      <div className="text-xs"><b>Motivo de emisión:</b> {motivo}{comentario.trim() && <div className="mt-1"><b>Comentario:</b> {comentario}</div>}</div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase text-text-muted">
            <th className="p-2 pl-3">Producto / concepto</th><th className="p-2 text-right">Cantidad</th><th className="p-2 text-right">Precio</th>
            <th className="p-2 text-right">Desc.</th><th className="p-2 text-right">ISV</th><th className="p-2 pr-3 text-right">Importe</th></tr></thead>
          <tbody>
            {filas.map((it, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="p-2 pl-3">{it.codigo && <span className="font-mono text-agro-green mr-1">{it.codigo}</span>}{it.descripcion}</td>
                <td className="p-2 text-right">{it.cantidad} {it.unidad || ""}</td>
                <td className="p-2 text-right">{fmtL(it.precio_unitario)}</td>
                <td className="p-2 text-right">{fmtL(it.descuento || 0)}</td>
                <td className="p-2 text-right">{Number(it.isv_pct) ? `${it.isv_pct}%` : "Exento"}</td>
                <td className="p-2 pr-3 text-right">{fmtL(Math.max(0, (Number(it.cantidad) || 0) * Number(it.precio_unitario) - (Number(it.descuento) || 0)))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs max-w-sm ml-auto">
        <Row l="Gravado 15%" v={fmtL(totals.s15)} /><Row l="ISV 15%" v={fmtL(totals.i15)} />
        {totals.s18 > 0 && <><Row l="Gravado 18%" v={fmtL(totals.s18)} /><Row l="ISV 18%" v={fmtL(totals.i18)} /></>}
        <Row l="Exento" v={fmtL(totals.sx)} /><div />
        <div className="col-span-2 border-t border-border pt-1 flex justify-between font-semibold"><span>TOTAL NOTA DE CRÉDITO</span>
          <span className="text-agro-yellow" data-testid="cn-resumen-total">{fmtL(totals.total)}</span></div>
      </div>
      <div className="text-xs bg-app border border-border rounded-md p-3" data-testid="cn-resumen-inventario">
        {esDev ? (recep.recibir_ahora
          ? <>Reingresan <b>{unidades}</b> unidades a la bodega <b>{bodega ? `${bodega.codigo} · ${bodega.nombre}` : "—"}</b> al emitir (una sola vez).</>
          : <>Devolución pendiente de recepción: el inventario se actualiza al confirmar la recepción física.</>)
          : <>Ajuste de valor: <b>sin movimiento de inventario</b>.</>}
      </div>
      <div className="flex flex-wrap justify-end gap-3">
        <button type="button" onClick={onVolver} className="min-h-[44px] px-4 text-sm border border-border rounded-md text-text-secondary">Volver a editar</button>
        <button type="button" onClick={onVistaPrevia} data-testid="cn-vista-previa-btn"
          className="min-h-[44px] px-4 text-sm border border-blue-500/50 text-blue-300 rounded-md flex items-center gap-1.5"><FilePdfIcon size={16} /> Vista previa</button>
        <button type="button" onClick={onEmitir} disabled={saving} data-testid="cn-confirmar-emision"
          className="min-h-[44px] px-5 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50">{saving ? "Emitiendo…" : "Confirmar y emitir"}</button>
      </div>
    </div>
  );
}

export function CNViewer({ cn, onClose, onReload }) {
  const party = cn.cliente_nombre || cn.proveedor_nombre;
  const rtn = cn.cliente_rtn || cn.proveedor_rtn;
  const [emailOpen, setEmailOpen] = useState(false);
  const [imprimir, setImprimir] = useState(false);
  return (
    <Modal title={`Nota de Crédito ${cn.kind === "ventas" ? "Venta" : "Compra"}`} onClose={onClose}>
      {imprimir && <ImprimirNc cn={cn} onClose={() => setImprimir(false)} />}
      {cn.kind === "ventas" && (
        <div className="flex flex-wrap justify-end gap-2 mb-2">
          <button onClick={() => setImprimir(true)} data-testid="cn-view-imprimir" title="Un solo documento: la nota de crédito y, después, la factura de origen"
            className="min-h-[40px] px-3 py-2 border border-agro-green/50 text-agro-green rounded-md text-xs font-medium flex items-center gap-1.5">
            <PrinterIcon size={14} /> Imprimir nota de crédito</button>
        </div>
      )}
      {cn.kind === "ventas" && cn.estado !== "anulada" && (
        <div className="flex justify-end mb-2">
          <button onClick={() => setEmailOpen(true)} data-testid="cn-send-email"
            className="px-3 py-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md text-xs flex items-center gap-1.5">
            <EnvelopeIcon size={14} /> Enviar por email
          </button>
        </div>
      )}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider text-agro-yellow">{cn.tipo === "otro" && cn.tipo_otro ? `Otro: ${cn.tipo_otro}` : cn.tipo}</div>
        <h3 className="font-heading text-xl font-semibold">{cn.numero}</h3>
        <div className="text-xs text-text-muted">
          {cn.documento_afectado?.tipo === "nota_debito" ? "Nota de débito origen" : "Factura origen"}:{" "}
          <span className="font-mono text-agro-green">{cn.documento_afectado?.numero || cn.factura_numero}</span> · Fecha de emisión NC: {fmtDate(cn.fecha)}
        </div>
        {cn.documento_afectado && (
          <div className="text-[11px] text-text-muted mt-0.5" data-testid="cn-view-doc">
            Emisión del documento: {fmtDate(cn.documento_afectado.fecha_emision)} · CAI: <span className="font-mono">{cn.documento_afectado.cai || "—"}</span>
          </div>
        )}
      </div>
      {cn.kind === "ventas" && (cn.documento_afectado?.id || cn.factura_id) && (
        <CNTraceability tipo={cn.documento_afectado?.tipo || "factura"}
          docId={cn.documento_afectado?.id || cn.factura_id} currentId={cn.id} />
      )}
      {cn.devolucion && (
        <div className="bg-app border border-border rounded-md p-3 mb-4 text-xs" data-testid="cn-view-devolucion">
          <div className="flex justify-between">
            <span className="uppercase tracking-wider text-[10px] text-text-muted">Devolución de productos</span>
            <DevolucionBadge cn={cn} />
          </div>
          {cn.devolucion.estado === "recibida" && (
            <div className="mt-1 text-text-secondary">
              Recibida el {fmtDate(cn.devolucion.fecha_recepcion)} por {cn.devolucion.recibido_por} en {cn.devolucion.bodega_nombre || cn.devolucion.bodega_codigo}
              <ul className="mt-1 list-disc pl-4">
                {(cn.devolucion.lineas || []).map((l) => (
                  <li key={l.linea}>{(cn.items[l.linea] || {}).descripcion}: {l.cantidad} · {l.condicion_label}{l.lote ? ` · lote ${l.lote}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {cn.devolucion.autorizacion && <div className="mt-1 text-text-muted">Autorización: {cn.devolucion.autorizacion} ({cn.devolucion.autorizado_por})</div>}
        </div>
      )}
      <div className="bg-app border border-border rounded-md p-3 mb-4 text-sm">
        <div>{party} {rtn && <span className="text-xs text-text-muted">· {rtn}</span>}</div>
        {cn.motivo && <div className="text-xs text-text-secondary mt-1">Motivo de emisión: {cn.motivo}</div>}
        {cn.comentario && <div className="text-xs text-text-secondary mt-1 whitespace-pre-wrap">Comentario: {cn.comentario}</div>}
        {cn.traslado_nc_id && <div className="text-xs text-text-muted mt-1">Emitida desde una solicitud de traslado a subcuenta NC</div>}
        {cn.proyecto_codigo && <div className="text-xs text-text-secondary mt-1">Proyecto: {cn.proyecto_codigo}</div>}
      </div>
      <div className="overflow-x-auto mb-4"><table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
          <th className="p-2">Código</th><th className="p-2">Descripción</th>
          <th className="p-2 text-right">Cant.</th><th className="p-2 text-right">Precio</th><th className="p-2 text-right">Total</th>
        </tr></thead>
        <tbody>
          {cn.items.map((it, i) => (
            <tr key={i} className="border-b border-border/30">
              <td className="p-2 font-mono text-xs">{it.codigo}</td>
              <td className="p-2 text-sm">{it.descripcion}</td>
              <td className="p-2 text-right">{it.cantidad}</td>
              <td className="p-2 text-right">{fmtL(it.precio_unitario)}</td>
              <td className="p-2 text-right font-medium">{fmtL(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <div className="flex justify-end">
        <div className="w-full max-w-xs space-y-1 text-sm">
          <Row l="Gravado 15%" v={fmtL(cn.subtotal_gravado_15)} />
          <Row l="ISV 15%" v={fmtL(cn.isv_15)} />
          {Number(cn.subtotal_gravado_18) > 0 && <Row l="Gravado 18%" v={fmtL(cn.subtotal_gravado_18)} />}
          {Number(cn.isv_18) > 0 && <Row l="ISV 18%" v={fmtL(cn.isv_18)} />}
          {Number(cn.subtotal_exento) > 0 && <Row l="Exento" v={fmtL(cn.subtotal_exento)} />}
          {Number(cn.subtotal_exonerado) > 0 && <Row l="Exonerado" v={fmtL(cn.subtotal_exonerado)} />}
          <div className="border-t border-border pt-2 mt-2 flex justify-between">
            <span className="font-heading text-base text-text-secondary">TOTAL</span>
            <span className="font-heading text-xl text-agro-yellow font-semibold">{fmtL(cn.total)}</span>
          </div>
          {cn.total_letras && (
            <div className="text-[10px] text-text-secondary text-right uppercase" data-testid="cn-total-letras">{cn.total_letras}</div>
          )}
        </div>
      </div>
      {cn.kind === "ventas" && <EmailHistory items={cn.email_history} />}
      {emailOpen && (
        <SendDocEmailModal title="Enviar nota de crédito por email" docLabel="Nota de crédito"
          endpoint={`/credit-notes/${cn.id}/send-email`} clienteId={cn.cliente_id}
          defaultSubject={`Nota de crédito ${cn.numero} · Agroport Honduras`}
          onClose={() => setEmailOpen(false)} onSent={() => onReload && onReload()} />
      )}
    </Modal>
  );
}

export function Modal({ title, onClose, children, m: mExt }) {
  const mLocal = useModalForm({ onClose: mExt ? undefined : onClose, abierto: !mExt });
  const m = mExt || mLocal;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cn-modal">
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
export const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;
export const Row = ({ l, v }) => <div className="flex justify-between"><span className="text-text-secondary text-xs">{l}</span><span className="text-text-primary text-sm">{v}</span></div>;
